import { isEqual, omit } from 'lodash';
import { type IFramework } from '@/framework';
import type { TextRenderResolution, TextRenderSnapshot } from '@/framework/services/text-render';
import {
    applyVectorTextLayout,
    createEmptyVectorEditingSnapshot,
    createVectorTextEditingCandidate,
    getVectorLayerHost,
    requestVectorSVGPreview,
    resolveVectorTextContentOffset,
    resolveVectorTextInlineSize,
} from '@/layers/vector-layer';
import { LayerType, TextAlignType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { EDITOR_PREWARM_CHARS } from '../constants';
import { getEmptyCaretX } from '../utils';
import {
    type TextEditorAdapter,
    type TextEditorContentGeometry,
    type TextEditorDisplayStyle,
    type TextEditorExitContext,
    type TextEditorLayerState,
    type TextEditorPreparedExit,
    type TextEditorUpdateReason,
} from './types';

/**
 * Vector Layer 接入通用 Text Editor 的宿主适配器。
 *
 * 适配器只维护 Vector 形态的 `editingLayer`，并通过真实 Edgeless Vector 宿主的公开 seam
 * 获取排版快照与布局结果；它不持有 Text Renderer，也不导入宿主的 input converter。
 */
export class VectorLayerEditorAdapter implements TextEditorAdapter {
    /**
     * 进入编辑态前是否已经存在持久化 textInfo。
     *
     * 这是本次会话的宿主形态标记，不是第二份当前值：候选文字始终只由 session 的
     * `editingLayer` 持有，适配器只需要知道空文本应当删除候选还是保留空 textInfo。
     */
    private hasOriginTextInfo = false;

    /**
     * 候选文字是否已写入 flatLayer，且尚未完成删空后的预览刷新
     */
    private hasWrittenCandidate = false;

    /**
     * 创建 Vector 宿主适配器。
     *
     * @param framework - 当前编辑器 framework。
     * @returns VectorLayerEditorAdapter 实例。
     */
    constructor(private readonly framework: IFramework) {}

    /**
     * 判断图层是否为 Vector 宿主。
     *
     * @param layer - 待判断的宿主图层。
     * @returns 图层类型为 Vector Layer 时返回 true。
     */
    matches(layer: ILayerProps): boolean {
        return layer.layerType === LayerType.VECTOR_LAYER;
    }

    /**
     * 读取 Vector 工作值中的纯文本。
     *
     * @param layer - 当前 Vector 工作值。
     * @returns textInfo 中的纯文本；无 textInfo 时返回空字符串。
     */
    getText(layer: ILayerProps): string {
        return (layer.data as IVectorLayerProps).textInfo?.text ?? '';
    }

    /**
     * 读取 Vector Text 供通用编辑视图使用的窄只读样式投影。
     *
     * @param layer - 当前 Vector 工作值。
     * @returns 与图层类型无关的文字样式投影。
     */
    getDisplayStyle(layer: ILayerProps): TextEditorDisplayStyle {
        const textInfo = (layer.data as IVectorLayerProps).textInfo;

        return {
            fontFamily: textInfo?.fontFamily ?? '',
            fontSize: textInfo?.fontSize ?? 0,
            lineSpacing: textInfo?.lineSpacing ?? 0,
            letterSpacing: textInfo?.letterSpacing ?? 0,
            bold: Boolean(textInfo?.bold),
            italic: Boolean(textInfo?.italic),
            color: textInfo?.color ?? '#000000ff',
        };
    }

    /**
     * 读取 Vector Text 的内容框几何投影。
     *
     * 偏移量与 Vector 组合 builder 共用 `resolveVectorTextContentOffset()`，
     * 光标、选区才会和同一份快照渲染出的正文严格对齐。
     *
     * @param layer - 当前 Vector 工作值。
     * @param snapshot - 当前工作值对应的排版快照。
     * @returns 与图层类型无关的内容框几何投影。
     */
    getContentGeometry(layer: ILayerProps, snapshot: TextRenderSnapshot | null): TextEditorContentGeometry {
        const textInfo = (layer.data as IVectorLayerProps).textInfo;

        return {
            // 光标行内起点相对内容框，padding 位移由 contentOffset 统一承担。
            emptyCaretX: getEmptyCaretX(textInfo?.textAlign ?? TextAlignType.LEFT, resolveVectorTextInlineSize(layer)),
            contentOffset: resolveVectorTextContentOffset(layer, snapshot),
        };
    }

    /**
     * 准备 Vector 进入文字编辑态所需的工作值与快照。
     *
     * 无 textInfo 的 Vector 在此创建同 ID 候选工作值；候选只保存在 session 中，
     * 是否写入 flatLayer 由后续编辑内容决定。进入时等待字体依赖终态，
     * 让首帧光标几何直接使用最终字体，但不改写图层几何。
     *
     * @param layer - 请求进入编辑态的真实 Vector Layer。
     * @returns 可提交的初始编辑状态；宿主不匹配时返回 null。
     */
    async prepareEnter(layer: ILayerProps): Promise<TextEditorLayerState | null> {
        if (!this.matches(layer)) return null;

        const editingLayer = { ...createVectorTextEditingCandidate(layer), __isOnTextEditing: true };
        await this.prewarmFonts(editingLayer);

        const host = getVectorLayerHost(this.framework, editingLayer.id);
        if (!host) return { editingLayer, snapshot: null };

        const resolution = await host.layoutTextAsync(editingLayer);
        return { editingLayer, snapshot: this.resolveSnapshot(editingLayer, resolution, null) };
    }

    private async prewarmFonts(layer: ILayerProps): Promise<void> {
        const { fontFamily, text } = (layer.data as IVectorLayerProps).textInfo!;
        const font = this.framework.service.font;
        if (!font.getByFamily(fontFamily) || font.hasRegisteredFallback(fontFamily)) return;

        await font.loadSubset(fontFamily, `${text}${EDITOR_PREWARM_CHARS}`);
    }

    /**
     * 记录候选基线并把首帧工作值交给画布。
     *
     * @param state - 已通过 manager 有效性校验的进入状态。
     * @param originLayer - 由 manager 保存的进入编辑态前原始图层。
     * @returns 无返回值。
     */
    commitEnter(state: TextEditorLayerState, originLayer: ILayerProps): void {
        this.hasOriginTextInfo = Boolean((originLayer.data as IVectorLayerProps).textInfo);
        this.hasWrittenCandidate = false;
        this.updateEditingLayer(state.editingLayer, state.snapshot);
    }

    /**
     * 把最新文本写入 Vector 工作值，不在输入事件内执行排版。
     *
     * 这里只替换候选 `textInfo.text`，不调用 Vector 宿主 Renderer，也不改写组合图层尺寸。Manager 会
     * 先同步保存返回的候选，再合并连续输入并调用 `resolveEditingState()` 统一计算文字和 Vector 几何。
     * 无 textInfo 的 Vector 没有可编辑文字，保持原工作值引用。
     *
     * @param layer - 当前 Vector 工作值。
     * @param text - 最新纯文本。
     * @returns 尚未排版的最新 Vector 工作值。
     */
    applyText(layer: ILayerProps, text: string): ILayerProps {
        const data = layer.data as IVectorLayerProps;
        if (!data.textInfo) return layer;

        const editingLayer: ILayerProps = {
            ...layer,
            data: { ...data, textInfo: { ...data.textInfo, text } },
        };
        return editingLayer;
    }

    /**
     * 使用真实 Vector 宿主重新解析当前工作值。
     *
     * @param layer - 当前 Vector 工作值。
     * @param currentSnapshot - 当前会话最后可用的排版快照。
     * @returns 当前工作值与 fallback 后的快照。
     */
    resolveEditingState(layer: ILayerProps, currentSnapshot: TextRenderSnapshot | null = null): TextEditorLayerState {
        return this.resolveLayerState(layer, currentSnapshot, true);
    }

    /**
     * 同步外部 Vector 更新，并保留只存在于会话中的候选文字。
     *
     * 尚未写入 flatLayer 的候选不会出现在外部数据里，因此外部图层缺少 textInfo 时
     * 必须沿用会话中的当前候选，session 始终是候选的唯一 source of truth。
     *
     * @param layer - 外部更新后的 Vector 图层。
     * @param current - 同步前的当前编辑状态。
     * @returns 同步后的工作值与 fallback 后的快照。
     */
    syncLayer(layer: ILayerProps, current: TextEditorLayerState): TextEditorLayerState {
        const state = this.resolveSyncedLayer(layer, current);
        // 外部更新不是新一轮编辑交互，只需要按最新工作值重画当前 Preview；
        // 用 refresh 才不会取消已经开始的正式 drawable 交接等待。
        if (state.snapshot) requestVectorSVGPreview(this.framework, 'refresh', state.editingLayer);
        return state;
    }

    private resolveSyncedLayer(layer: ILayerProps, current: TextEditorLayerState): TextEditorLayerState {
        const data = layer.data as IVectorLayerProps;
        const candidate = (current.editingLayer.data as IVectorLayerProps).textInfo;
        const editingLayer: ILayerProps = {
            ...layer,
            __isOnTextEditing: true,
            data: data.textInfo || !candidate ? data : { ...data, textInfo: candidate },
        };

        return this.resolveLayerState(editingLayer, current.snapshot, false);
    }

    /**
     * 将尚未排版的最新 Vector 文字候选同步到画布数据层。
     *
     * 原图层没有 textInfo 时，空候选不能被持久化；如果此前已经写入过非空候选，本次删空会只摘除
     * textInfo，并保留编辑期间由外部 API 更新的其他 Vector 字段。该数据阶段不请求 Preview，避免在
     * Manager 延后排版前构建一次使用旧几何的组合 SVG。
     *
     * @param layer - 已应用最新文本、尚未排版的 Vector 工作值。
     * @returns 无返回值。
     */
    updateEditingData(layer: ILayerProps): void {
        if (!this.hasOriginTextInfo && this.getText(layer) === '') {
            this.restoreLayerWithoutCandidate(layer, false);
            return;
        }
        if (!this.hasOriginTextInfo) this.hasWrittenCandidate = true;
        this.framework.service.frame.addOrUpdateLayer(layer, undefined, true);
        this.framework.service.frame.setActiveLayer(layer.id);
    }

    /**
     * 把当前 Vector 工作值写入画布并请求连续预览。
     *
     * 原本没有 textInfo 且候选仍为空时不写 flatLayer；若之前写过，则立即把 flatLayer 还原成
     * 不含候选文字的当前 Vector，保证未确认的候选不会产生持久化数据。
     * 已解析的 snapshot 由 Text Editor 用于光标和选区几何；Vector Preview 仍通过真实组合 builder
     * 生成完整图形与文字 SVG，因此这里不把 snapshot 直接传给 Preview。
     *
     * @param layer - 当前 Vector 工作值。
     * @param _snapshot - 当前工作值对应的排版快照。
     * @param reason - 本次更新是用户编辑还是宿主 Text Render Invalidation 后的重解析。
     * @returns 无返回值。
     */
    updateEditingLayer(
        layer: ILayerProps,
        _snapshot: TextRenderSnapshot | null,
        reason: TextEditorUpdateReason = 'edit',
    ): void {
        if (!this.hasOriginTextInfo && this.getText(layer) === '') {
            this.restoreLayerWithoutCandidate(layer);
            return;
        }

        if (!this.hasOriginTextInfo) this.hasWrittenCandidate = true;
        // pending/failed 只代表本轮几何尚未 ready，仍写入最新工作值；
        // Preview 自己会在无法构建时保留上一份有效画面。
        //
        // Text Render Invalidation 只让最新输入失效，不是新一轮编辑：用 refresh 保留编辑会话内由
        // writeHistory API 发起的正式 drawable 交接等待，避免 Preview 永久驻留。
        requestVectorSVGPreview(this.framework, reason === 'invalidation' ? 'refresh' : 'update', layer);
        this.updateEditingData(layer);
    }

    /**
     * 完成 Vector Text 退出事务
     *
     * Vector 继续使用既有的最终字体排版与持久化规则，只把准备和提交顺序封装在 Adapter 内，避免
     * Manager 了解候选 textInfo、组合 Preview 和 history 细节。
     *
     * @param context - 退出开始前捕获的 Vector 编辑状态
     * @returns 退出已完成时返回 true；准备失败返回 false
     */
    async finalizeExit(context: TextEditorExitContext): Promise<boolean> {
        const source = this.framework.service.flatLayer.getLayer(context.editingLayer.id);
        if (!source || source.lock) {
            this.resetEditingState();
            return true;
        }
        const sourceBaseline = this.framework.service.flatLayer.getLayer(source.id, true);
        const sourceState = this.resolveSyncedLayer(sourceBaseline, {
            editingLayer: context.editingLayer,
            snapshot: null,
        });
        const prepared = await this.prepareExit({ ...context, editingLayer: sourceState.editingLayer });
        const latest = this.framework.service.flatLayer.getLayer(source.id);
        if (!latest || latest.lock) {
            this.resetEditingState();
            return true;
        }
        if (!isEqual(this.framework.service.flatLayer.getLayer(source.id, true), sourceBaseline)) return false;
        if (!prepared) return false;

        this.commitExit(prepared);
        return true;
    }

    /**
     * 准备退出编辑态所需的最终 Vector 数据。
     *
     * 原本没有 textInfo 的候选最终为空时摘除候选且不写历史；其余情况等待字体终态，
     * 用最终字体收敛几何后原子提交，即使数据未变化也提交正式 drawable，只跳过历史。
     *
     * @param context - 退出所需的会话图层状态。
     * @returns 可同步提交的退出内容。
     */
    private async prepareExit(context: TextEditorExitContext): Promise<TextEditorPreparedExit | null> {
        const editingData = context.editingLayer.data as IVectorLayerProps;
        const hasOriginTextInfo = Boolean((context.originLayer.data as IVectorLayerProps).textInfo);

        // 空候选退出不产生数据与历史；Vector 不套用 Text Layer 的“删除空文字图层”行为。
        // 摘除候选而不是写回捕获的 originLayer，避免回滚编辑期间外部 API 已经提交的其他字段。
        if (!hasOriginTextInfo && this.getText(context.editingLayer) === '') {
            return {
                type: 'commit',
                originLayer: context.originLayer,
                finalLayer: { ...context.editingLayer, data: omit(editingData, 'textInfo'), __isOnTextEditing: false },
                hasLayerChange: false,
            };
        }

        const host = getVectorLayerHost(this.framework, context.editingLayer.id);
        const laidOut = host
            ? applyVectorTextLayout(context.editingLayer, await host.layoutTextAsync(context.editingLayer))
            : context.editingLayer;
        const finalLayer: ILayerProps = { ...laidOut, __isOnTextEditing: false };
        const hasLayerChange = !isEqual(
            omit(context.originLayer, '__isOnTextEditing'),
            omit(finalLayer, '__isOnTextEditing'),
        );

        return { type: 'commit', originLayer: context.originLayer, finalLayer, hasLayerChange };
    }

    /**
     * 同步提交最终 Vector 数据、正式 drawable 与历史。
     *
     * @param prepared - 已通过当前 Adapter 最新图层校验的退出内容。
     * @returns 无返回值。
     */
    private commitExit(prepared: TextEditorPreparedExit): void {
        // Vector 永远不会因为文字为空被删除，因此这里只处理 commit 分支。
        if (prepared.type !== 'commit') {
            this.resetEditingState();
            return;
        }

        requestVectorSVGPreview(this.framework, 'commit', prepared.finalLayer);
        this.framework.service.frame.addOrUpdateLayer(prepared.finalLayer);
        this.framework.service.frame.setActiveLayer(prepared.finalLayer.id);
        if (prepared.hasLayerChange) {
            this.framework.service.history.write(
                omit(prepared.originLayer, '__isOnTextEditing'),
                omit(prepared.finalLayer, '__isOnTextEditing'),
            );
        }
        this.resetEditingState();
    }

    /**
     * 清理适配器持有的会话标记。
     *
     * @returns 无返回值。
     */
    private resetEditingState(): void {
        this.hasOriginTextInfo = false;
        this.hasWrittenCandidate = false;
    }

    /**
     * 把已写入 flatLayer 的空候选从当前 Vector 上摘除。
     *
     * 这里刻意不写回进入编辑态时捕获的原始图层，否则编辑期间通过外部 API 同步进来的
     * 其他 Vector 字段（例如填充色）会被一并回滚。
     *
     * 这里固定用 update：摘除候选是把画面改回“无文字 Vector”的一次内容变化，与失效通知的
     * 纯刷新不同。数据阶段先摘除候选，预览阶段再消费标记，确保清空画面后不再重复处理。
     *
     * @param layer - 当前带有空候选 textInfo 的 Vector 工作值。
     * @param preview - 是否发送一次移除文字后的组合 Preview；纯数据输入阶段传 false，排版发布阶段传 true。
     * @returns 无返回值。
     */
    private restoreLayerWithoutCandidate(layer: ILayerProps, preview = true): void {
        if (!this.hasWrittenCandidate) return;

        const restored: ILayerProps = { ...layer, data: omit(layer.data as IVectorLayerProps, 'textInfo') };
        if (preview) {
            this.hasWrittenCandidate = false;
            requestVectorSVGPreview(this.framework, 'update', restored);
        }

        this.framework.service.frame.addOrUpdateLayer(restored, undefined, true);
        this.framework.service.frame.setActiveLayer(restored.id);
    }

    /**
     * 通过真实 Edgeless Vector 宿主解析四态结果并执行编辑态 fallback。
     *
     * @param layer - 当前 Vector 工作值。
     * @param currentSnapshot - 当前 session 最后可用的快照。
     * @param applyLayout - ready 时是否把布局结果写回工作值几何。
     * @returns 可供 Text Editor 直接消费的图层与快照。
     */
    private resolveLayerState(
        layer: ILayerProps,
        currentSnapshot: TextRenderSnapshot | null,
        applyLayout: boolean,
    ): TextEditorLayerState {
        const host = getVectorLayerHost(this.framework, layer.id);
        if (!host) return { editingLayer: layer, snapshot: currentSnapshot };

        const resolution = host.layoutText(layer);

        return {
            // applyVectorTextLayout 只在 ready 时改写交叉轴几何，pending/failed/empty 保留当前几何。
            editingLayer: applyLayout ? applyVectorTextLayout(layer, resolution) : layer,
            snapshot: this.resolveSnapshot(layer, resolution, currentSnapshot),
        };
    }

    /**
     * 把宿主 Renderer 的四态结果收敛为编辑态可用的单一快照。
     *
     * @param layer - 当前 Vector 工作值。
     * @param resolution - 宿主 Renderer 的四态布局结果。
     * @param currentSnapshot - 当前 session 最后可用的快照。
     * @returns ready 使用当前结果，empty 使用宿主空编辑快照，pending/failed 保留上一份有效快照。
     */
    private resolveSnapshot(
        layer: ILayerProps,
        resolution: TextRenderResolution<TextRenderSnapshot>,
        currentSnapshot: TextRenderSnapshot | null,
    ): TextRenderSnapshot | null {
        // ready/empty 都描述当前输入；前者直接发布，后者按 Vector padding/alignment 补空编辑几何。
        if (resolution.status === 'ready') return resolution.value;
        if (resolution.status === 'empty') return createEmptyVectorEditingSnapshot(layer);

        // pending/failed 没有当前输入的几何，必须继续使用本编辑会话已接受的快照。
        return currentSnapshot;
    }
}
